import {
  Target,
  Eye,
  Heart,
  Zap,
  Globe,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import TestimonialsSlider from "../components/TestimonialsSlider";
import FAQSection from "../components/FAQSection";
import { usePopup } from "../context/PopupContext";
import { useState, useCallback } from "react";

const values = [
  {
    icon: Target,
    title: "Precision",
    desc: "Every application we file is meticulously prepared with attention to detail, maximizing your chances of successful registration.",
  },
  {
    icon: Eye,
    title: "Transparency",
    desc: "We keep you informed at every step. No hidden fees, no surprises. You always know exactly where your application stands.",
  },
  {
    icon: Heart,
    title: "Client-First Approach",
    desc: "Your success is our success. We tailor our services to your specific needs, providing personalized guidance throughout the process.",
  },
  {
    icon: Zap,
    title: "Efficiency",
    desc: "Our streamlined processes and experienced team ensure your trademark application is filed quickly without compromising quality.",
  },
  {
    icon: Globe,
    title: "Comprehensive Coverage",
    desc: "From trademark registration to company formation, we offer a full suite of intellectual property and business services.",
  },
  {
    icon: ShieldCheck,
    title: "Reliability",
    desc: "Thousands of businesses trust us with their brand protection. Our track record speaks for itself in successful registrations.",
  },
];

const stats = [
  { num: "97%", label: "Approval Success" },
  { num: "98%", label: "Client Satisfaction" },
  { num: "50", label: "States Covered" },
  { num: "24/7", label: "Expert Support" },
];

export default function AboutPage() {
  const { openPopup } = usePopup();
  const [valuesIndex, setValuesIndex] = useState(0);
  const [isAnimatingValues, setIsAnimatingValues] = useState(false);
  const [valuesDrag, setValuesDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
  });

  const totalValues = values.length;
  const maxValuesIndex = totalValues - 3;

  const valuesNext = useCallback(() => {
    if (isAnimatingValues) return;
    setIsAnimatingValues(true);
    setValuesIndex((prev) => (prev >= maxValuesIndex ? 0 : prev + 1));
    setTimeout(() => setIsAnimatingValues(false), 500);
  }, [isAnimatingValues, maxValuesIndex]);

  const valuesPrev = useCallback(() => {
    if (isAnimatingValues) return;
    setIsAnimatingValues(true);
    setValuesIndex((prev) => (prev <= 0 ? maxValuesIndex : prev - 1));
    setTimeout(() => setIsAnimatingValues(false), 500);
  }, [isAnimatingValues, maxValuesIndex]);

  const goToValuesSlide = useCallback(
    (index) => {
      if (isAnimatingValues || index > maxValuesIndex) return;
      setIsAnimatingValues(true);
      setValuesIndex(index);
      setTimeout(() => setIsAnimatingValues(false), 500);
    },
    [isAnimatingValues, maxValuesIndex],
  );

  // Drag handlers for values slider
  const handleValuesMouseDown = useCallback((e) => {
    setValuesDrag({ isDragging: true, startX: e.clientX, currentX: 0 });
  }, []);

  const handleValuesMouseMove = useCallback(
    (e) => {
      if (!valuesDrag.isDragging) return;
      const diff = e.clientX - valuesDrag.startX;
      setValuesDrag((prev) => ({ ...prev, currentX: diff }));
    },
    [valuesDrag.isDragging, valuesDrag.startX],
  );

  const handleValuesMouseUp = useCallback(() => {
    if (!valuesDrag.isDragging) return;
    const threshold = 50;
    if (valuesDrag.currentX > threshold) {
      valuesPrev();
    } else if (valuesDrag.currentX < -threshold) {
      valuesNext();
    }
    setValuesDrag({ isDragging: false, startX: 0, currentX: 0 });
  }, [valuesDrag.isDragging, valuesDrag.currentX, valuesPrev, valuesNext]);

  const handleValuesMouseLeave = useCallback(() => {
    if (valuesDrag.isDragging) {
      setValuesDrag({ isDragging: false, startX: 0, currentX: 0 });
    }
  }, [valuesDrag.isDragging]);

  return (
    <main data-testid="about-page">
      <SEOHead
        title="About Us | Trademark Legal Forum - Your Trusted IP Partner"
        description="Learn about Trademark Legal Forum's mission to make brand protection accessible to every business. Expert IP professionals with thousands of successful trademark registrations."
        ogImage="https://static.prod-images.emergentagent.com/jobs/90678b74-8893-4692-bfc7-d7847fcc33f8/images/75874694900d1a5f30eb84fdc682a7cc3e0e18642e96009ba65ddd97cb4bfe0d.png"
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/90678b74-8893-4692-bfc7-d7847fcc33f8/images/75874694900d1a5f30eb84fdc682a7cc3e0e18642e96009ba65ddd97cb4bfe0d.png"
            alt="About us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A1128]/85" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">
              About Us
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tight leading-none mb-6">
              Your Trusted Partner in{" "}
              <span className="gold-gradient-text">Brand Protection</span>
            </h1>
            <p className="text-base md:text-lg text-[#A0AABF] leading-relaxed max-w-2xl">
              We are a dedicated team of intellectual property professionals
              committed to helping businesses protect their most valuable
              assets, their brands.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section
        data-testid="about-mission"
        className="py-20 md:py-32 bg-[#FFFFFF]"
      >
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">
                Our Mission
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 font-['Outfit'] tracking-tight mb-6">
                Making Brand Protection Accessible to Every Business
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                At Trademark Legal Forum, we believe that every business,
                regardless of size, deserves access to professional intellectual
                property protection. Our mission is to simplify the complex
                world of trademarks, copyrights, and patents, making it
                accessible and affordable for entrepreneurs and established
                businesses alike.
              </p>
              <p className="leading-relaxed text-gray-600">
                We combine deep legal expertise with modern technology and a
                client-first approach to deliver trademark services that are
                efficient, transparent, and results-driven. Every brand has a
                story, and we are here to make sure that story stays yours.
              </p>
            </div>
            <div className="relative">
              <div className="bg-[#121E3D] border border-[#1D2D50] rounded-sm p-10">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold font-['Outfit'] mb-1">
                        Thousands of Trademarks Filed
                      </h4>
                      <p className="text-[#A0AABF] text-sm">
                        Successfully filed thousands of trademark applications
                        across every industry.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold font-['Outfit'] mb-1">
                        High Client Satisfaction Rate
                      </h4>
                      <p className="text-[#A0AABF] text-sm">
                        Our clients consistently rate us as their top choice for
                        IP services.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-3 h-3 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold font-['Outfit'] mb-1">
                        Nationwide Coverage Across the USA
                      </h4>
                      <p className="text-[#A0AABF] text-sm">
                        Comprehensive coverage for businesses operating in any
                        US state.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-12">
            <button
              data-testid="about-mission-register-btn"
              onClick={() => openPopup()}
              className="bg-[#D4AF37] text-[#0A1128] font-bold py-3 px-8 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wide"
            >
              Register My Trademark
            </button>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section
        data-testid="about-values"
        className="py-20 md:py-32 bg-[#0A1128]"
      >
        <div className="container mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">
              Our Values
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight">
              What Drives Us Every Day
            </h2>
          </div>

          <div className="relative">
            <style>{`
              .values-slider {
                --translate-percent: -34%;
              }
              @media (max-width: 639px) {
                .values-slider {
                  --translate-percent: -108%;
                }
              }
              @media (min-width: 640px) and (max-width: 1023px) {
                .values-slider {
                  --translate-percent: -50%;
                }
              }
            `}</style>
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out gap-6 values-slider cursor-grab active:cursor-grabbing"
                style={{
                  "--slide-index": valuesIndex,
                  transform: `translateX(calc(var(--slide-index) * var(--translate-percent)))`,
                }}
                onMouseDown={handleValuesMouseDown}
                onMouseMove={handleValuesMouseMove}
                onMouseUp={handleValuesMouseUp}
                onMouseLeave={handleValuesMouseLeave}
              >
                {values.map((val, i) => {
                  const Icon = val.icon;
                  return (
                    <div
                      key={i}
                      data-testid={`about-value-${i}`}
                      className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-0.666rem)] flex-shrink-0 bg-[#000000] border border-[#1D2D50] rounded-sm p-8 hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-300"
                    >
                      <Icon size={28} className="text-[#D4AF37] mb-4" />
                      <h3 className="text-lg font-bold text-white font-['Outfit'] mb-3">
                        {val.title}
                      </h3>
                      <p className="text-[#A0AABF] text-sm leading-relaxed">
                        {val.desc}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Nav arrows */}
            <button
              onClick={valuesPrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={valuesNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-10 h-10 bg-[#0A1128] border border-[#1D2D50] rounded-full flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors hidden sm:flex"
            >
              <ChevronRight size={18} />
            </button>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: maxValuesIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToValuesSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    valuesIndex === i ? "bg-[#D4AF37] w-6" : "bg-[#1D2D50]"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Stats Section */}
      <section
        data-testid="stats-section"
        className="py-20 md:py-28 bg-[white] border-y border-[#1D2D50]/30"
      >
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#D4AF37] font-['Outfit'] mb-2">
                  {stat.num}
                </p>
                <p className="text-gray-600 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section
        data-testid="about-approach"
        className="py-20 md:py-32 bg-[#0D1729]"
      >
        <div className="container mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#D4AF37] mb-4 font-['Outfit']">
              Our Approach
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-['Outfit'] tracking-tight mb-8">
              Built on Expertise. Powered by Technology.
            </h2>
            <p className="text-[#A0AABF] leading-relaxed mb-6 text-base md:text-lg">
              We have built a service that combines the precision of experienced
              legal professionals with the efficiency of modern processes. Every
              trademark application we handle goes through a rigorous review
              process to ensure accuracy and maximize the likelihood of
              approval.
            </p>
            <p className="text-[#A0AABF] leading-relaxed mb-8 text-base md:text-lg">
              Our clients range from individual entrepreneurs launching their
              first business to established corporations managing extensive
              trademark portfolios. Regardless of scope, every client receives
              the same level of dedication, expertise, and personalized
              attention. We are not just a service provider; we are your
              long-term partner in brand protection.
            </p>
            <button
              data-testid="about-approach-register-btn"
              onClick={() => openPopup()}
              className="bg-[#D4AF37] text-[#0A1128] font-bold py-3 px-8 rounded-sm text-sm transition-all duration-300 hover:bg-[#F3E5AB] hover:-translate-y-0.5 shadow-[0_4px_14px_0_rgba(212,175,55,0.39)] font-['Outfit'] tracking-wide"
            >
              Register My Trademark
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <TestimonialsSlider /> */}

      {/* FAQ Section */}
      <FAQSection />
    </main>
  );
}
